import {Image} from 'primereact/image'
import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import {useDropzone} from 'react-dropzone'
import TranslateUtils from '../../i18n/TranslateUtils'
import FlexBox from '../layout/FlexBox'
import {showLoading, stopLoading} from '../loading/LoadingService'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {uploadImage} from './ImageEndPoint'

const ImageUploader = forwardRef(
    (
        {id, multiple = true, extraData, callBack = () => {}, initialImage},
        ref,
    ) => {
        useImperativeHandle(ref, () => ({
            value: handleGetImages(),
            getValue: handleGetImages,
            changeValue: newValue => setImageUrls(newValue),
        }))
        const {translateKeys, translateCommons} = TranslateUtils()
        const [imageUrls, setImageUrls] = useState(
            initialImage
                ? !!multiple
                    ? [...initialImage]
                    : [initialImage]
                : [],
        )

        const imageRef = useRef(null)

        const onDrop = useCallback(async acceptedFiles => {
            if (!isArrayAndNotEmpty(acceptedFiles)) {
                showNotification(
                    severityNotification.warn,
                    'File not supported !',
                )
                return
            }

            if (callBack) {
                const uploadedImages = await handleUploadImage(acceptedFiles)
                if (uploadedImages?.length > 0) {
                    if (!!multiple) {
                        let holderData = imageUrls.filter(
                            url => !uploadedImages.includes(url),
                        )

                        setImageUrls([...uploadedImages, ...holderData])
                        callBack(id, [...uploadedImages, ...holderData])
                    } else {
                        setImageUrls([...uploadedImages])
                        callBack(id, uploadedImages[0])
                    }
                } else {
                    showNotification(
                        severityNotification.error,
                        'Fail to proceed images !',
                    )
                }
            }
        }, []) // eslint-disable-line react-hooks/exhaustive-deps

        const handleGetImages = () => {
            if (imageUrls?.length > 0) {
                if (!!multiple) {
                    return imageUrls
                } else {
                    return imageUrls[0]
                }
            }

            return null
        }

        const {getRootProps, getInputProps} = useDropzone({
            onDrop,
            multiple: true,
            accept: {
                'image/jpeg': ['.jpeg', '.jpg'],
                'image/png': ['.png'],
            },
            maxFiles: 10,
        })

        const handleUploadImage = async files => {
            if (!files || files.length < 1) {
                return
            }

            showLoading(translateKeys('Processing images'))

            let imageUrls = []
            for (let i = 0; i < files.length; i++) {
                const imageFile = files[i]

                let bodyFormData = new FormData()
                bodyFormData.append('image', imageFile)

                const imageResponse = await uploadImage(bodyFormData)
                if (
                    imageResponse &&
                    !!imageResponse.success &&
                    imageResponse.data
                ) {
                    imageUrls.push(imageResponse.data.display_url)
                }
            }

            stopLoading()

            return imageUrls
        }

        const handleRemoveImage = useCallback(
            imageUrl => {
                let tmp = imageUrls.filter(x => x !== imageUrl)

                if (!!multiple) {
                    callBack(id, tmp)
                    setImageUrls([...tmp])
                } else {
                    callBack(id, '')
                    setImageUrls([])
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [id, imageUrls, multiple],
        )

        return (
            <React.Fragment>
                <div className="image-uploader mb-3" {...getRootProps()}>
                    <input {...getInputProps()} />
                    <FlexBox
                        flex-direction="row"
                        justify-content="center"
                        align-items="center"
                        className="pt-3">
                        <img
                            src={require('../../assets/images/image_uploader/uploader.png')}
                            alt={'Uploader'}
                            style={{
                                height: '50px',
                            }}
                        />
                    </FlexBox>

                    <h5 className="text-center">
                        {translateCommons('Drag and drop images here')}
                    </h5>
                    <p className="text-center">
                        ( {translateKeys('Supported Formats')}: JPG, PNG, JPEG )
                    </p>

                    <div className="mt-2 mb-5">
                        <div className="flex justify-content-center align-items-center">
                            <div
                                className="px-3 bg-teal-400"
                                style={{
                                    width: '10vw',
                                    marginBottom: -9,
                                    borderRadius: 8,
                                }}>
                                <p
                                    className="text-center mb-0 font-bold"
                                    style={{color: 'white'}}>
                                    {translateCommons('or')}
                                </p>
                            </div>
                        </div>
                        <hr className="p-0 m-0" />
                    </div>

                    <div className="col-12" style={{cursor: 'pointer'}}>
                        <h5
                            className="text-center"
                            // style={{color: 'darkcyan'}}
                            style={{color: 'gray'}}>
                            {translateCommons('Click here to upload')}
                        </h5>
                    </div>
                </div>

                {imageUrls?.length > 0 && (
                    <div>
                        <h5 className="font-bold mb-1">
                            {translateCommons('You have uploaded')}{' '}
                            {imageUrls?.length} {translateCommons('image')}(s)
                        </h5>
                        <div className="horizontal-scroll my-2">
                            {imageUrls.map((url, idx) => (
                                <div
                                    key={idx}
                                    className="image-uploaded-item mr-2 p-3">
                                    <div className="image-removal flex justify-content-around">
                                        <div
                                            className="image-removal-item"
                                            onClick={() =>
                                                handleRemoveImage(url)
                                            }>
                                            <i
                                                className="pi pi-trash"
                                                style={{
                                                    color: 'red',
                                                    fontSize: '1.5rem',
                                                }}></i>
                                        </div>
                                        <div
                                            className="image-removal-item"
                                            onClick={() =>
                                                imageRef.current.show()
                                            }>
                                            <i
                                                className="pi pi-eye"
                                                style={{
                                                    color: 'green',
                                                    fontSize: '1.5rem',
                                                }}></i>
                                        </div>
                                    </div>
                                    <Image
                                        ref={imageRef}
                                        src={url}
                                        alt={id}
                                        height={'60'}
                                        width={'60'}
                                        preview
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </React.Fragment>
        )
    },
)

export default ImageUploader
